import {
  PaymentReturnNotice,
  readHostedCheckoutReturnState,
} from "@triedge/payments";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { domainRouterClient } from "../../../api/domainRouterClient";
import { resolveCognitoProfile } from "../../../utils/cognito-profile";
import {
  createHostedPaymentSession,
  createHostedPortalSession,
} from "../../../utils/payments";
import {
  type ParentTuitionSnapshot,
  type TuitionDocumentSnapshot,
  loadParentTuitionSnapshot,
} from "../shared/tuitionData";
import { ACTIVE_TUITION_ACADEMIC_YEAR } from "../shared/academicYear";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { ParentTuitionSurface } from "./ParentTuitionSurface";
import {
  buildParentTuitionViewModel,
  formatCurrency,
  type ParentTuitionCheckoutRequest,
  type ParentTuitionDialogId,
} from "./parentTuitionViewModel";

const ACADEMIC_YEAR = ACTIVE_TUITION_ACADEMIC_YEAR;

const resolveCheckoutUrl = (kind: "success" | "cancel"): string => {
  const url = new URL(window.location.href);
  url.searchParams.set("checkout", kind);
  return url.toString();
};

const resolvePaymentProfile = () => resolveCognitoProfile();

const extractPaymentErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  if (error && typeof error === "object") {
    const message =
      "message" in error
        ? String((error as { message?: unknown }).message ?? "").trim()
        : "";
    if (message) {
      return message;
    }
    if (
      Array.isArray((error as { errors?: Array<{ message?: unknown }> }).errors)
    ) {
      const nested = (error as { errors?: Array<{ message?: unknown }> }).errors
        ?.map((entry) => String(entry?.message ?? "").trim())
        .filter(Boolean)
        .join("; ");
      if (nested) {
        return nested;
      }
    }
  }
  return String(error ?? "").trim() || "Unknown payment error";
};

const normalizeFileSegment = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "file";

const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

function LoadingSkeleton() {
  return (
    <div
      data-testid="parent-tuition-loading"
      data-route-family="internal-owned"
      data-route-loading="true"
      data-route-ready="false"
      className="source-faithful-shell"
      style={{ "--ystnj-source-faithful-max-width": "1024px" } as CSSProperties}
    >
      <div className="source-faithful-container">
        <div className="h-20 animate-pulse rounded-xl bg-muted" />
        <div className="h-72 animate-pulse rounded-xl bg-muted" />
        <div className="h-14 animate-pulse rounded-xl bg-muted" />
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="h-56 animate-pulse rounded-xl bg-muted" />
          <div className="h-56 animate-pulse rounded-xl bg-muted" />
        </div>
        <div className="h-96 animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}

type ParentTuitionDialogState = ParentTuitionDialogId | null;

export function ParentTuitionOwnedPage() {
  const location = useLocation();
  const [snapshot, setSnapshot] = useState<ParentTuitionSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeDialog, setActiveDialog] =
    useState<ParentTuitionDialogState>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    setSnapshot(null);

    void loadParentTuitionSnapshot(ACADEMIC_YEAR)
      .then((nextSnapshot) => {
        if (!active) {
          return;
        }
        setSnapshot(nextSnapshot);
      })
      .catch((loadFailure) => {
        if (!active) {
          return;
        }
        setSnapshot(null);
        setLoadError(
          loadFailure instanceof Error
            ? loadFailure.message
            : "Failed to load parent tuition data.",
        );
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const run = async (id: string, action: () => Promise<void>) => {
    setBusyAction(id);
    setErrorMessage(null);
    try {
      await action();
    } catch (error) {
      setErrorMessage(extractPaymentErrorMessage(error));
    } finally {
      setBusyAction(null);
    }
  };

  const downloadDocument = async (tuitionDocument: TuitionDocumentSnapshot) => {
    if (!snapshot) {
      throw new Error("Tuition data is no longer available.");
    }

    if (
      tuitionDocument.downloadMode === "derived" &&
      tuitionDocument.derivedTransactionId
    ) {
      const transaction =
        snapshot.transactions.find(
          (entry) => entry.id === tuitionDocument.derivedTransactionId,
        ) ?? null;
      const body = [
        "YSTNJ Payment Receipt",
        "",
        `Family: ${snapshot.familyName ?? "Family"}`,
        `Receipt: ${tuitionDocument.name}`,
        `Date: ${tuitionDocument.uploadedAtLabel}`,
        `Reference: ${transaction?.referenceCode ?? "Not available"}`,
        `Amount: ${transaction ? formatCurrency(Math.abs(transaction.amountCents)) : "Not available"}`,
        `Status: ${transaction?.isStripeCorrelated ? "Stripe-synced" : "Recorded internally"}`,
      ].join("\n");

      triggerDownload(
        new Blob([body], { type: "text/plain;charset=utf-8" }),
        tuitionDocument.fileName ??
          `${normalizeFileSegment(tuitionDocument.name)}.txt`,
      );
      return;
    }

    if (!tuitionDocument.remoteDocumentId) {
      throw new Error("This tuition document is not downloadable yet.");
    }

    const response = await domainRouterClient.query(
      "getFamilyDocumentDownloadUrl",
      {
        documentId: tuitionDocument.remoteDocumentId,
      },
    );
    const anchor = globalThis.document.createElement("a");
    anchor.href = String(response.downloadUrl ?? "");
    anchor.download = tuitionDocument.fileName ?? tuitionDocument.name;
    anchor.target = "_blank";
    anchor.rel = "noopener";
    globalThis.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const openBillingPortal = () => {
    void run("portal", async () => {
      const url = await createHostedPortalSession({
        moduleId: "tuition",
        returnUrl: window.location.href,
      });
      window.location.assign(url);
    });
  };

  const startHostedCheckout = (request: ParentTuitionCheckoutRequest) => {
    if (!snapshot) {
      return;
    }

    let busyId: string;
    let amountCents: number;
    let paymentMode: "full" | "overdue" | "custom" | "installment";
    let description: string;
    let lineItemName: string;
    let lineItemDescription: string;
    let installmentId: string | null = null;

    if (request.kind === "full") {
      busyId = "checkout-full";
      amountCents = Math.max(snapshot.netBalanceCents, 0);
      paymentMode = "full";
      description = "Parent portal tuition payment in full";
      lineItemName = "Tuition payment in full";
      lineItemDescription =
        "Parent portal hosted checkout for the current family balance";
    } else if (request.kind === "overdue") {
      busyId = "checkout-overdue";
      amountCents = Math.max(snapshot.overdueCents, 0);
      paymentMode = "overdue";
      description = "Parent portal overdue tuition payment";
      lineItemName = "Overdue tuition payment";
      lineItemDescription =
        "Parent portal hosted checkout for the overdue balance";
    } else if (request.kind === "custom") {
      busyId = "checkout-custom";
      amountCents = request.amountCents;
      paymentMode = "custom";
      description = "Parent portal custom tuition payment";
      lineItemName = "Custom tuition payment";
      lineItemDescription =
        "Parent portal hosted checkout for a custom or pay-ahead amount";
    } else {
      const installment =
        snapshot.installmentSchedule.find(
          (entry) => entry.id === request.installmentId,
        ) ?? null;
      if (!installment) {
        setErrorMessage(
          "Select an installment before continuing to hosted checkout.",
        );
        return;
      }
      busyId = "checkout-installment";
      amountCents = installment.remainingAmountCents;
      paymentMode = "installment";
      description = "Parent portal installment-targeted tuition payment";
      lineItemName = installment.label || "Installment payment";
      lineItemDescription = `Hosted checkout for the ${installment.dueDateLabel} installment`;
      installmentId = installment.id;
    }

    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      return;
    }

    void run(busyId, async () => {
      const profile = resolvePaymentProfile();
      const url = await createHostedPaymentSession({
        moduleId: "tuition",
        operationKey: "tuition_checkout",
        mode: "payment",
        currency: "usd",
        successUrl: resolveCheckoutUrl("success"),
        cancelUrl: resolveCheckoutUrl("cancel"),
        customerEmail: profile?.email,
        customerReferenceId: profile?.userSub,
        description,
        lineItems: [
          {
            name: lineItemName,
            description: lineItemDescription,
            quantity: 1,
            unitAmountCents: amountCents,
          },
        ],
        metadata: {
          route: "/parent/portal/tuition",
          academicYear: snapshot.academicYear || ACADEMIC_YEAR,
          actorSub: profile?.userSub ?? "",
          paymentMode,
          ...(installmentId ? { installmentId } : {}),
        },
      });
      window.location.assign(url);
    });
  };

  const hostedReturnState = useMemo(
    () => readHostedCheckoutReturnState(location.search),
    [location.search],
  );
  const viewModel = useMemo(
    () => (snapshot ? buildParentTuitionViewModel(snapshot) : null),
    [snapshot],
  );
  const hostedReturnNotice = (
    <PaymentReturnNotice
      state={hostedReturnState}
      noticeKey="parent-tuition"
      successTitle="Payment handed off successfully"
      successBody="Stripe returned successfully. Your tuition statement will refresh as the payment is reconciled into the family ledger."
      cancelTitle="Payment was not completed"
      cancelBody="No tuition payment was finalized. You can review the family statement below or try hosted checkout again when ready."
      guidanceBody="Tuition balances update from the family billing ledger after Stripe reconciliation completes. If the statement still looks stale, refresh this page after a few minutes instead of retrying the charge immediately."
      primaryAction={
        snapshot ? (
          <button
            type="button"
            onClick={() => setActiveDialog("statement")}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-iron-grey shadow-sm"
          >
            View Statement
          </button>
        ) : null
      }
    />
  );

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (loadError && !snapshot) {
    return (
      <div
        data-testid="parent-tuition-source-faithful-root"
        data-route-family="internal-owned"
        data-route-ready="true"
        className="source-faithful-shell"
        style={
          { "--ystnj-source-faithful-max-width": "1024px" } as CSSProperties
        }
      >
        <div className="mx-auto max-w-4xl px-4 py-8 pb-20 sm:px-6">
          {hostedReturnNotice}
          <SupportErrorMessage
            data-testid="parent-tuition-dashboard-error"
          >
            {loadError}
          </SupportErrorMessage>
        </div>
      </div>
    );
  }

  if (!snapshot || !viewModel) {
    return (
      <div
        data-testid="parent-tuition-source-faithful-root"
        data-route-family="internal-owned"
        data-route-ready="true"
        className="source-faithful-shell"
        style={
          { "--ystnj-source-faithful-max-width": "1024px" } as CSSProperties
        }
      >
        <div className="mx-auto max-w-4xl px-4 py-8 pb-20 sm:px-6">
          {hostedReturnNotice}
          <p
            data-testid="parent-tuition-dashboard-empty"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            No tuition records were returned for this family.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ParentTuitionSurface
      model={viewModel}
      activeDialog={activeDialog}
      busyAction={busyAction}
      errorMessage={errorMessage}
      hostedReturnNotice={hostedReturnNotice}
      onOpenDialog={(dialogId) => setActiveDialog(dialogId)}
      onCloseDialog={() => setActiveDialog(null)}
      onStartPayment={startHostedCheckout}
      onOpenBillingPortal={openBillingPortal}
      onDownloadDocument={(document, busyId) => {
        void run(busyId, () => downloadDocument(document));
      }}
    />
  );
}

export function ParentTuitionOwnedRoutes() {
  return (
    <Routes>
      <Route index element={<ParentTuitionOwnedPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
